import { useState, useMemo, useCallback, useEffect } from 'react';
import { AppointmentsTable } from 'components/Appointment/Table';
import { NextAppointmentCard } from 'components/Appointment/NextAppointment';
import { LastVisitCard } from 'components/Appointment/LastVisitCard';
import { AppointmentsTabs } from 'components/Appointment/Tabs';
import { ConfirmAppointmentModal } from 'components/Appointment/ConfirmAppointmentModal';
import { PortalSpinner } from 'components/PortalSpinner';
import { APPOINTMENT_STATUS } from 'utils/constants';
import { connect } from 'react-redux';
import { AppointmentActions } from 'store/actions';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { requestMapper } from 'store/Appointments/helper';
import moment from 'moment';

const enhancer = connect(null, { ...AppointmentActions })

type Tab = 'upcoming' | 'history';

const Appointments = ({ getAppointments, updateAppointment }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmingId, setConfirmingId] = useState(null)
  const [appointmentToConfirmId, setAppointmentToConfirmId] = useState(null)
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam === 'history' || tabParam === 'upcoming' ? tabParam : 'upcoming'
  );
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAppointments()
  }, [])

  const fetchAppointments = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await getAppointments();
      setAppointments(data);
    } catch (error) {
      toast.error(error.errors || 'Failed to load appointments. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [])

  const { upcoming, history } = useMemo(() => {
    const upcoming = [];
    const history = [];
    const now = moment();
    for (const appt of appointments) {
      const start = moment(appt.appointmentAt, 'YYYY-MM-DDTHH:mm:ss');
      if (!start.isValid()) continue

      const end = start.clone().add(appt.durationMinutes, 'minutes');

      if (end.isBefore(now))
        history.push(appt);
      else
        upcoming.push(appt);

    }
    // Sort by full timestamp so same-day appointments come back in real time order.
    // new Date() translation doesn't require for comparison here
    const timeOf = (a) =>
      a.appointmentAt ? new Date(a.appointmentAt).getTime() : 0;
    upcoming.sort((a, b) => timeOf(a) - timeOf(b));
    history.sort((a, b) => timeOf(b) - timeOf(a));
    return { upcoming, history };
  }, [appointments]);

  const rows = activeTab === 'upcoming' ? upcoming : history;

  const handleTabChange = useCallback(
    (tab: Tab) => {
      setActiveTab(tab);
      setSearchParams((params) => {
        const nextParams = new URLSearchParams(params);
        nextParams.set('tab', tab);
        return nextParams;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (tabParam === 'history' || tabParam === 'upcoming') {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const openConfirmAppointmentModal = useCallback((id) => {
    setAppointmentToConfirmId(id);
  }, []);

  const closeConfirmAppointmentModal = useCallback(() => {
    if (confirmingId) return;
    setAppointmentToConfirmId(null);
  }, [confirmingId]);

  const confirmAppointment = useCallback(async () => {
    const id = appointmentToConfirmId;
    if (!id) return;

    setConfirmingId(id)

    try {
      let { data } = await updateAppointment(id, 'confirm')

      const request = requestMapper(data);
      setAppointments(_appointments =>
        _appointments.map((appointment) =>
          appointment.id === id
            ? { ...appointment, requests: [request, ...(appointment.requests || [])] }
            : appointment
        )
      );

    } catch (error) {
      toast.error(error.errors || 'Failed to confirm appointment. Please try again later.');
    }

    setConfirmingId(null)
    setAppointmentToConfirmId(null)
  }, [appointmentToConfirmId, updateAppointment])

  // Cards above the tabs:
  // - Next Appointment: scheduled AND not yet ended (start + duration > now).
  //   Keeps the card visible during the appointment so "Happening now" can fire.
  //   Hides it once the duration has elapsed even if backend status hasn't flipped.
  //   `upcoming` is sorted by appointmentAt asc, so .find returns the soonest.
  // - Last Visit only surfaces actually-completed visits — cancelled history
  //   rows aren't a "visit".
  const now = Date.now();
  const nextUpcoming = upcoming.find((a) => {
    if (a.status !== APPOINTMENT_STATUS.SCHEDULED) return false;
    if (!a.appointmentAt) return false;

    const start = moment(a.appointmentAt, 'YYYY-MM-DDTHH:mm:ss');
    if (!start.isValid()) return false;

    const end = start.clone().add(a.durationMinutes, 'minutes');
    return end.valueOf() > now;
  });
  const mostRecentPast = history.find((a) => a.status === APPOINTMENT_STATUS.COMPLETED);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">My Appointments</h1>
        <p className="text-sm text-secondary-500 mt-1">View your upcoming and past appointments.</p>
      </div>

      {/* Both cards render once the initial fetch completes — each handles
          its own empty state when there's no matching appointment. */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <NextAppointmentCard
            appointment={nextUpcoming ?? null}
            confirmingId={confirmingId}
            onConfirm={openConfirmAppointmentModal}
          />
          <LastVisitCard appointment={mostRecentPast ?? null} />
        </div>
      )}

      <AppointmentsTabs
        activeTab={activeTab}
        upcomingCount={upcoming.length}
        historyCount={history.length}
        onTabChange={handleTabChange}
      />

      {/* Table */}
      <div className="overflow-hidden">
        {loading ? (
          <PortalSpinner />
        ) :
          rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-secondary-400">
              No {activeTab} appointments.
            </div>
          ) : (
            <AppointmentsTable rows={rows} confirmingId={confirmingId} onConfirm={openConfirmAppointmentModal} />
          )}
      </div>

      <ConfirmAppointmentModal
        open={Boolean(appointmentToConfirmId)}
        loading={Boolean(confirmingId)}
        onClose={closeConfirmAppointmentModal}
        onConfirm={confirmAppointment}
      />
    </div>
  );
};

export default enhancer(Appointments);
